import React from 'react';
import { SuCarousel, WebinarCard } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { WebinarData } from 'src/types/state/webinar';
import { parseHtmlToReactForCard, hasDescriptionContent } from 'src/utils/htmlParser';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, WEBINAR_STRING_ID } from 'src/constants/localization';
import { WEBINAR_REGISTRATION_STATUS } from 'src/constants/webinar';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { mergeUrlParameters } from 'src/utils/routing';
import { REF_TAG } from 'src/constants/refTag';

/** Webinar carousel props */
interface WebinarCarouselProps {
  webinars: WebinarData[];
  onWebinarClick: (webinarId: string) => void;
}

const WebinarCarousel: React.FC<WebinarCarouselProps> = ({ webinars, onWebinarClick }) => {
  const [webinarBundle] = useBundle(BUNDLE_NAME.WEBINAR);
  const { clickMetric } = useMetrics();

  // Format webinar data for WebinarCard component
  const formatWebinarForCard = (webinar: WebinarData) => {
    // Format timings: date on line 1, time range + timezone on line 2
    const timings = `${webinar.date}\n${webinar.startTime} - ${webinar.endTime} ${webinar.userTimezone || ''}`.trim();

    // Determine button configuration based on registration status
    const getButtonConfig = () => {
      if (webinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTERED) {
        return null; // No button for registered state
      }

      if (webinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.JOIN) {
        return {
          text: t(webinarBundle, WEBINAR_STRING_ID.JOIN_BUTTON_TEXT),
          onClick: () => {
            // Track JOIN button click
            clickMetric({
              feature: FeatureName.WebinarButtonClick,
              metadata: {
                url: window.location.href,
                webinarId: webinar.webinarId,
                webinarTitle: webinar.title,
                registrationStatus: webinar.registrationStatus,
                buttonLocation: 'card',
                buttonAction: 'join',
              },
            });

            // Open registration URL in new tab with ref tag
            if (webinar.registrantURL) {
              const urlWithRefTag = mergeUrlParameters(webinar.registrantURL, `?ref_=${REF_TAG.WEBINAR_CARD_JOIN}`);
              window.open(urlWithRefTag, '_blank');
            }
          },
          variant: 'primary' as const,
        };
      }

      // REGISTER state
      return {
        text: t(webinarBundle, WEBINAR_STRING_ID.REGISTER_BUTTON_TEXT),
        onClick: (cardId?: string) => {
          // Track REGISTER button click
          clickMetric({
            feature: FeatureName.WebinarButtonClick,
            metadata: {
              url: window.location.href,
              webinarId: webinar.webinarId,
              webinarTitle: webinar.title,
              registrationStatus: webinar.registrationStatus,
              buttonLocation: 'card',
              buttonAction: 'register',
            },
          });

          if (cardId) {
            onWebinarClick(cardId);
          }
        },
        variant: 'primary' as const,
      };
    };

    return {
      cardId: webinar.webinarId,
      banner: t(webinarBundle, WEBINAR_STRING_ID.BANNER_TEXT),
      title: webinar.title,
      timings,
      contentHeading: hasDescriptionContent(webinar.description)
        ? t(webinarBundle, WEBINAR_STRING_ID.SESSION_OVERVIEW_TEXT)
        : undefined,
      contentDescription: parseHtmlToReactForCard(webinar.description),
      contentIcon: hasDescriptionContent(webinar.description) ? 'check' as const : undefined,
      button: getButtonConfig(),
      registeredText: webinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTERED
        ? t(webinarBundle, WEBINAR_STRING_ID.ALREADY_REGISTERED_TEXT)
        : undefined,
      registeredIcon: webinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.REGISTERED
        ? 'info-circle' as const
        : undefined,
      className: 'h-full',
    };
  };

  return (
    <SuCarousel
      items={webinars}
      getItemKey={(webinar) => webinar.webinarId}
      renderItem={(webinar) => (
        <div className="flex justify-center">
          <WebinarCard {...formatWebinarForCard(webinar)} />
        </div>
      )}
      layout={{
        xsmall: { cols: 1, rows: 1 },
        small: { cols: 2, rows: 1 },
        medium: { cols: 3, rows: 1 },
        large: { cols: 3, rows: 1 },
      }}
      className="mx-auto max-w-7xl px-su-space-08"
    />
  );
};

export default WebinarCarousel;
